/**
 * Customer Active List Configuration
 * Columns, expanded columns, and filter definitions
 */
import type { TFunction } from 'i18next'
import type {
    DataTableColumn,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import {
    createCurrencyFormatter,
    createDateFormatter,
    createNumberFormatter,
    createChipFormatter,
} from '@/components/ui/data-table-customized'
import type {
    CustomerActive,
    CustomerActiveService,
} from '@/modules/Customer/types'
import {
    getEmployeesOptionsPaginated,
    getEmployeesDefaultOptions,
} from '@/modules/Employee/services'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Link } from 'react-router-dom'
import { DEPARTMENT_IDS } from '@/constants/departments'
import {
    renderEmployeeNames,
    renderIndustry,
    SERVICE_STATUS_CHIP_COLORS,
    createCsFollowersFilter,
    createIndustryFilter,
    createProductGroupFilter,
    createSourceFilters,
} from './customer-list-shared-config'

// ============================================================================
// Main Customer Columns
// ============================================================================

export const getCustomerActiveColumns = (
    t: TFunction
): DataTableColumn<CustomerActive>[] => [
    {
        key: 'customer_name',
        title: t('customer_name'),
        width: 300,
        pinned: 'left',
        cellClassName: 'truncate',
        sortable: true,
        render: (_, row) => (
            <Link
                to={generatePathDatacentral(paths.customerDetail, {
                    id: row.customer_id,
                })}
                className="text-primary hover:underline uppercase"
            >
                {row.customer_id} - {row.company_name || row.customer_name}
            </Link>
        ),
    },
    {
        key: 'customer_sale_followers',
        title: t('customer_sales_followers'),
        width: 200,
        render: (value) => renderEmployeeNames(value, t('un-sales-assigned')),
    },
    {
        key: 'customer_cs_followers',
        title: t('customer_cs_followers'),
        width: 200,
        render: (value) => renderEmployeeNames(value, t('un-cs-assigned')),
    },
    {
        key: 'customer_industry',
        title: t('customer_industry'),
        width: 150,
        sortable: true,
        render: (value) =>
            renderIndustry(value, t('customer_industry.unknown')),
    },
    {
        key: 'total_payment',
        title: t('total_payment'),
        width: 150,
        cellClassName: 'text-right',
        sortable: true,
        formatter: createNumberFormatter({}),
    },
    {
        key: 'first_service_start_date',
        title: t('first_service_start_date'),
        width: 150,
        cellClassName: 'text-right',
        sortable: true,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'customer_updated_at',
        title: t('updated_at'),
        width: 150,
        cellClassName: 'text-right text-gray-400 italic',
        sortable: true,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY HH:mm' }),
    },
]

// ============================================================================
// Expanded Service Columns
// ============================================================================

export const getServiceColumns = (
    t: TFunction
): DataTableColumn<CustomerActiveService>[] => [
    {
        key: 'service_name',
        title: t('service'),
        width: 350,
        render: (_, row) => (
            <Link
                to={generatePathDatacentral(paths.serviceDetail, {
                    id: row.service_id,
                })}
                className="text-primary hover:underline uppercase"
            >
                {row.service_id} - {row.product_group_name}
            </Link>
        ),
    },
    {
        key: 'service_start_date',
        title: t('service_start_date'),
        width: 130,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'service_status',
        title: t('service_status'),
        width: 150,
        formatter: createChipFormatter<CustomerActiveService>({
            colorMap: SERVICE_STATUS_CHIP_COLORS(t),
            defaultConfig: { color: 'default' },
            size: 'sm',
        }),
    },
    {
        key: 'service_price_register',
        title: t('service_price_register'),
        width: 150,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: 'VND' }),
    },
]

// ============================================================================
// Filter Configuration
// ============================================================================

export const getCustomerActiveFilterItems = (
    t: TFunction
): FilterItemConfig[] => [
    // Sale followers with prepended special options (custom asyncOptions)
    {
        name: 'customer_sale_followers',
        type: 'async-select',
        label: t('customer_sales_followers'),
        placeholder: t('select_customer_sale_followers'),
        asyncOptions: async (query, page) => {
            const result = await getEmployeesOptionsPaginated(
                query,
                [
                    DEPARTMENT_IDS.ACCOUNT,
                    DEPARTMENT_IDS.SALES,
                    DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
                ],
                undefined,
                page
            )
            if (page === 1 && !query) {
                return {
                    ...result,
                    options: [
                        { label: t('no-sale-assignee'), value: '-1' },
                        { label: t('sale-assignee-off'), value: '-2' },
                        ...result.options,
                    ],
                }
            }
            return result
        },
        getDefaultOptions: getEmployeesDefaultOptions,
        multiple: true,
    },
    createCsFollowersFilter(t),
    createIndustryFilter(t),
    createProductGroupFilter(t, 'product_group_names'),
    {
        name: 'has_sla',
        type: 'select',
        label: t('has_sla'),
        placeholder: t('select_has_sla'),
        options: [
            { label: t('yes'), value: 'true' },
            { label: t('no'), value: 'false' },
        ],
    },
    ...createSourceFilters(t),
]

// ============================================================================
// Filter Default Values
// ============================================================================

export const CUSTOMER_ACTIVE_FILTER_DEFAULTS = {
    customer_sale_followers: [],
    customer_cs_followers: [],
    customer_industry: [],
    product_group_names: [],
    customer_source: [],
    customer_source_type: [],
    has_sla: undefined,
}

// ============================================================================
// Sort Configuration
// ============================================================================

export const CUSTOMER_ACTIVE_SORT_CONFIG = {
    defaultSort: {
        sort_by: 'first_service_start_date',
        sort_order: 'desc' as const,
    },
    sortableFields: [
        'customer_name',
        'customer_industry',
        'total_payment',
        'first_service_start_date',
        'customer_updated_at',
        'has_cs',
    ],
}
